// Контекст оформления: выбранная палитра и размер шрифта.
// Оба значения доезжают до CSS через <html>: data-theme подменяет палитру
// (src/styles/themes.css), --fs-scale растягивает шкалу --fs-* (tokens.css).
//
// Вид может прийти и из адреса — `?theme=…&fs=…&sp=…`, как это делает публичная
// ссылка на поездку. Тогда он действует на один визит: страницу показываем
// такой, какой её отправили, но в чужое хранилище не пишем. Своё оформление
// читателя вернётся, как только он уйдёт с присланного адреса, а если он
// возьмётся крутить ползунки прямо здесь — выбор станет его и переживёт визит.
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createStorage } from '../store';
import {
  appearanceFromParams,
  hasStoredAppearance,
  readAppearance,
  writeAppearance,
  prefersDarkScheme,
  type Appearance,
  type AppearancePatch,
} from './appearance';
import {
  FONT_SCALES,
  SPACE_SCALES,
  THEMES,
  isThemeId,
  type ThemeId,
  type ThemeMeta,
} from './themes';

export interface ThemeControls {
  theme: ThemeId;
  /** Метаданные выбранной темы — имя и светлая/тёмная. */
  meta: ThemeMeta;
  /** Индекс ступени размера шрифта в FONT_SCALES. */
  fontScaleIndex: number;
  fontScale: number;
  /** Индекс ступени отступов в SPACE_SCALES. */
  spaceScaleIndex: number;
  spaceScale: number;
  setTheme(id: ThemeId): void;
  setFontScaleIndex(index: number): void;
  setSpaceScaleIndex(index: number): void;
  /** Вид задан адресом (присланная ссылка), а не выбран самим читателем. */
  fromLink: boolean;
}

const ThemeContext = createContext<ThemeControls | undefined>(undefined);

/** Оформление из адреса. Вне браузера адреса нет — и подсказки тоже. */
function linkAppearance(): AppearancePatch | null {
  if (typeof window === 'undefined') return null;
  return appearanceFromParams(new URLSearchParams(window.location.search));
}

export function ThemeProvider({ children }: { children: ReactNode }): ReactElement {
  const storage = useMemo(() => createStorage(), []);
  const [own, setOwn] = useState<Appearance>(() => readAppearance(storage, prefersDarkScheme()));
  const [link, setLink] = useState<AppearancePatch | null>(linkAppearance);

  // Настройки в хранилище — след собственного выбора. Пока его нет, писать туда
  // нечего: пустая запись превратила бы «ничего не выбирал» в «выбрал ровно
  // умолчания», и присланный вид больше некому было бы отличить от своего.
  const [chosen, setChosen] = useState(() => hasStoredAppearance(storage));

  const appearance = useMemo<Appearance>(() => ({ ...own, ...link }), [own, link]);

  // useLayoutEffect, а не useEffect: атрибут должен встать до первой отрисовки,
  // иначе выбранная тёмная тема моргнёт светлой на один кадр.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.dataset.theme = appearance.theme;
    root.style.setProperty('--fs-scale', String(FONT_SCALES[appearance.fontScaleIndex]));
    root.style.setProperty('--sp-scale', String(SPACE_SCALES[appearance.spaceScaleIndex]));
  }, [appearance]);

  useLayoutEffect(() => {
    if (chosen) writeAppearance(storage, own);
  }, [storage, own, chosen]);

  // Свежее значение нужно прямо в момент правки, а не то, что попало
  // в замыкание при отрисовке.
  const linkRef = useRef(link);
  linkRef.current = link;

  /**
   * Любая правка ползунком — это выбор читателя. Присланный вид при этом не
   * откатывается целиком, а становится его собственным: человек смотрел на
   * крупный кегль и поменял только палитру — кегль обязан остаться крупным.
   */
  const choose = useCallback((patch: AppearancePatch) => {
    setOwn((prev) => ({ ...prev, ...linkRef.current, ...patch }));
    setLink(null);
    setChosen(true);
  }, []);

  const setTheme = useCallback(
    (id: ThemeId) => {
      if (isThemeId(id)) choose({ theme: id });
    },
    [choose],
  );

  const setFontScaleIndex = useCallback(
    (index: number) => {
      if (!Number.isInteger(index) || index < 0 || index >= FONT_SCALES.length) return;
      choose({ fontScaleIndex: index });
    },
    [choose],
  );

  const setSpaceScaleIndex = useCallback(
    (index: number) => {
      if (!Number.isInteger(index) || index < 0 || index >= SPACE_SCALES.length) return;
      choose({ spaceScaleIndex: index });
    },
    [choose],
  );

  const value = useMemo<ThemeControls>(
    () => ({
      theme: appearance.theme,
      meta: THEMES[appearance.theme],
      fontScaleIndex: appearance.fontScaleIndex,
      fontScale: FONT_SCALES[appearance.fontScaleIndex],
      spaceScaleIndex: appearance.spaceScaleIndex,
      spaceScale: SPACE_SCALES[appearance.spaceScaleIndex],
      setTheme,
      setFontScaleIndex,
      setSpaceScaleIndex,
      fromLink: link !== null,
    }),
    [appearance, link, setTheme, setFontScaleIndex, setSpaceScaleIndex],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeControls {
  const ctx = useContext(ThemeContext);
  if (ctx === undefined) {
    throw new Error('useTheme() должен использоваться внутри <ThemeProvider>');
  }
  return ctx;
}
